import React from 'react';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';
import { StickyContainer, Sticky } from 'react-sticky';

import DisplayLocation from '../../../../../../components/common/DisplayLocation';
import DisplayDate from '../../../../../../components/common/DisplayDate';
import ButtonQuantities from './ButtonQuantities';
import PrimaryButton from '../../../../../../components/common/forms/Button/PrimaryButton';
import BorderlessButton from '../../../../../../components/common/forms/Button/BorderlessButton';
import useReserver from './useReserver';
import { ProductX } from '~/src/types/Misc';
import { Colors } from '~/src/scss/common/style';
import { Link } from 'react-router-dom';
import { DATE_FORMAT } from '~/src/utils/constants';
import moment from 'moment';
import resetArrow from '~/src/static/img/clockClockwise.svg';

import clockClockwise from '../../../../../../static/img/clockClockwise.svg';

interface Props {
  item: ProductX;
  id: string;
  index: number;
}

export default function ReserveProductBlock({ item, id, index }: Props): JSX.Element {
  const reserver = useReserver(item, id);
  const handleColor = (): string => {
    if (reserver.status === 'unsaved') {
      return '#FDF3E8';
    } else if (reserver.status === 'error') {
      return Colors.redOrange;
    } else if (reserver.status === 'success') {
      return '#F2FBF3';
    } else {
      return 'none';
    }
  };

  const renderNotes = (notes) => {
    if (notes != null) {
      let string = '';
      const objectFormNotes = JSON.parse(notes);
      objectFormNotes.forEach((element) => {
        string += `${element.Date}: ${element.Quantities}\n`;
      });
      return (
        <div>
          <Link to={`/inventory/list-for-generic/${item.orderProduct.product.genericProductId}`} target="_blank">
            {string}
          </Link>
        </div>
      );
    }
    return '';
  };

  return (
    <Container>
      <StickyContainer style={{ display: 'flex' }}>
        <Sticky>
          {({ style }): JSX.Element => (
            <ProductWrapper style={{ position: 'absolute', ...style, width: 300, padding: 10, borderRadius: 10 }}>
              <ProductContent>
                {index}.
                <StyledLink to={`/products/edit/${item.orderProduct.product.genericProductId}`} target="_blank">
                  <b>{item.productName}</b>
                </StyledLink>
                <br />
                {item.orderProduct.expiry ? moment(item.orderProduct.expiry).format(DATE_FORMAT) : null}
              </ProductContent>
              <QuantityWrapper>
                <Quantities>
                  <table>
                    <thead>
                      <tr>
                        <StyledThQuantities></StyledThQuantities>
                        <StyledThQuantities>Du</StyledThQuantities>
                        <StyledThQuantities>Dp</StyledThQuantities>
                        <StyledThQuantities>Cu</StyledThQuantities>
                      </tr>
                    </thead>
                    <tbody style={{ borderRadius: '5px' }}>
                      <tr>
                        <CellSmall>
                          <StyledLink target="_blank" to={`/orders/calculations/${item.orderProduct.orderId}`}>
                            Order
                          </StyledLink>
                        </CellSmall>
                        <CellSmall>{reserver?.itemData?.orderProduct?.quantityDu}</CellSmall>
                        <CellSmall>x</CellSmall>
                        <CellSmall>x</CellSmall>
                        {/* <CellSmall>{reserver?.itemData?.orderProduct?.quantityCu}</CellSmall> */}
                      </tr>
                      <tr>
                        <CellSmall>
                          <StyledLink
                            target="_blank"
                            to={`/inventory/list-for-generic/${item.orderProduct.product.genericProductId}`} target="_blank"
                          >
                            {' '}
                            Reserved
                          </StyledLink>
                        </CellSmall>
                        <CellSmall>{reserver.duReserved}</CellSmall>
                        <CellSmall>{reserver.dpReserved}</CellSmall>
                        <CellSmall>{reserver.cuReserved}</CellSmall>
                      </tr>
                      <tr>
                        <CellSmall>Reserve Preview</CellSmall>
                        <CellSmall>{reserver.duReservePreview}</CellSmall>
                        <CellSmall>{reserver.dpReservePreview}</CellSmall>
                        <CellSmall>{reserver.cuReservePreview}</CellSmall>
                      </tr>
                      <tr>
                        <CellSmall>To Reserve</CellSmall>
                        <CellSmall>
                          {(reserver?.itemData?.orderProduct?.quantityDu ?? 0) - reserver.duReservePreview}
                        </CellSmall>
                        <CellSmall>x</CellSmall>
                        <CellSmall>x</CellSmall>
                      </tr>
                    </tbody>
                  </table>
                </Quantities>
                <MainControls>
                  {reserver.bufferTransactions.length > 0 ? (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <PrimaryButton
                        title="Save"
                        onClick={reserver.prepareDtoSendAndUpdateScreen}
                        isSubmitting={reserver.loading}
                      />
                      <div style={{ marginLeft: '10px' }}></div>
                      <BorderlessButton
                        iconSize={25}
                        icon={resetArrow}
                        onClick={reserver.clearAllBufferTransactions}
                      />
                    </div>
                  ) : (
                    <QuantitiesText>No changes</QuantitiesText>
                  )}
                </MainControls>
              </QuantityWrapper>
            </ProductWrapper>
          )}
        </Sticky>
        <Table statusColor={handleColor()}>
          <Head>
            <tr>
              <StyledTh>Info</StyledTh>
              <StyledTh></StyledTh>
              <StyledTh>Avaliable</StyledTh>
              <StyledTh>
                Already
                <br />
                Reserved
              </StyledTh>
              <StyledTh>
                Reserve
                <br />
                Preview
              </StyledTh>
              <StyledTh>Actions</StyledTh>
            </tr>
          </Head>
          <Body>
            {reserver.itemData.distincts.map((data) => {
              const buffer = reserver.bufferTransactions.find(
                (bufferTransaction) => bufferTransaction.tempId === data.availableQuantities.tempId,
              );
              const maxDu = Math.min(
                (data?.availableQuantities?.fullDuUnits ?? 0) + (data?.reservedQuantities?.fullDuUnits ?? 0),
                (reserver?.itemData?.orderProduct?.quantityDu ?? 0) - reserver.duReservePreview ?? 0,
              );
              const maxDp =
                (data?.availableQuantities?.singleDpUnits ?? 0) + (data?.reservedQuantities?.singleDpUnits ?? 0);
              const maxCu =
                (data?.availableQuantities?.singleCuUnits ?? 0) + (data?.reservedQuantities?.singleCuUnits ?? 0);
              return (
                <TRow key={uuid()}>
                  <StyledTd>
                    <Span>
                      <DisplayDate
                        date={data?.availableQuantities.expirationDate ?? data?.reservedQuantities.expirationDate}
                      />
                    </Span>
                    <Span>
                      <DisplayLocation
                        chamber={
                          data?.availableQuantities?.warehouseLocation?.warehouseChamber?.number ??
                          data?.reservedQuantities?.warehouseLocation?.warehouseChamber?.number
                        }
                        column={
                          data?.availableQuantities?.warehouseLocation?.column ??
                          data?.reservedQuantities?.warehouseLocation?.column
                        }
                        letter={
                          data?.availableQuantities?.warehouseLocation?.letter ??
                          data?.reservedQuantities?.warehouseLocation?.letter
                        }
                        level={
                          data?.availableQuantities?.warehouseLocation?.level ??
                          data?.reservedQuantities?.warehouseLocation?.level
                        }
                      />
                    </Span>
                    <Span>
                      {data.availableQuantities.lot ?? data.reservedQuantities.lot
                        ? data.availableQuantities.lot ?? data.reservedQuantities.lot
                        : '---'}
                    </Span>
                    <StyledLink to={`/purchase-orders/edit/${data.availableQuantities?.purchaseOrder?.id}`}>
                      PO: {data.availableQuantities?.purchaseOrder?.orderNumber}
                    </StyledLink>
                    <Span>
                      {data.availableQuantities?.warehouse?.name ??
                        data.reservedQuantities?.warehouse?.name}
                    </Span>
                  </StyledTd>

                  <StyledTd>
                    <Span>DU</Span>
                    <Span>DP</Span>
                    <Span>CU</Span>
                  </StyledTd>

                  <StyledTd>
                    <Span>{data.availableQuantities.fullDuUnits}</Span>
                    <Span>{data.availableQuantities.singleDpUnits}</Span>
                    <Span>{data.availableQuantities.singleCuUnits}</Span>
                  </StyledTd>

                  <StyledTd>
                    <Span>{data.reservedQuantities.fullDuUnits}</Span>
                    <Span>{data.reservedQuantities.singleDpUnits}</Span>
                    <Span>{data.reservedQuantities.singleCuUnits}</Span>
                  </StyledTd>

                  <StyledTd>
                    <Span>{buffer?.fullDuUnits}</Span>
                    <Span>{buffer?.singleDpUnits}</Span>
                    <Span>{buffer?.singleCuUnits}</Span>
                  </StyledTd>

                  <StyledTd>
                    <ActionWrapper>
                      <div>
                        <Span>
                          <ButtonQuantities
                            tempId={data.availableQuantities.tempId}
                            maxNumber={maxDu}
                            unitType="du"
                            reserve={reserver.setQuantity}
                            clear={reserver.clearUnitFromBufferTransaction}
                          />
                        </Span>
                        <Span>
                          <ButtonQuantities
                            tempId={data.availableQuantities.tempId}
                            maxNumber={maxDp}
                            unitType="dp"
                            reserve={reserver.setQuantity}
                            clear={reserver.clearUnitFromBufferTransaction}
                          />
                        </Span>
                        <Span>
                          <ButtonQuantities
                            tempId={data.availableQuantities.tempId}
                            maxNumber={maxCu}
                            unitType="cu"
                            reserve={reserver.setQuantity}
                            clear={reserver.clearUnitFromBufferTransaction}
                          />
                        </Span>
                      </div>
                    </ActionWrapper>
                  </StyledTd>
                  <StyledTd>
                    <CellButton>
                      <BorderlessButton
                        icon={clockClockwise}
                        title="Reset"
                        variant="link"
                        onClick={reserver.clearAllUnits}
                        vertical={true}
                        iconSize={35}
                      />
                    </CellButton>
                  </StyledTd>
                </TRow>
              );
            })}
          </Body>
        </Table>
      </StickyContainer>
    </Container>
  );
}

interface ContainerProps {
  statusColor: string;
}
const Container = styled.div<ContainerProps>`
  min-height: 300px;
  margin-bottom: 50px;
`;

const StyledLink = styled(Link)`
  text-align: center;
  color: ${Colors.darkBrown};
  font-size: 15px;
  line-height: 17px;
  font-weight: 700;
`;

const ProductWrapper = styled.div`
  position: absolute;
  width: 300;
  margin-right: 25px;
  border: 1px solid #eee;
  background: #f4f4f4;
  padding: 23px 15px;
`;

const QuantitiesText = styled.p`
  color: #a7a7a7;
  font-size: 15px;
  text-align: center;
  font-weight: 700;
`;

const StyledThQuantities = styled.th`
  border-left: 1px solid #fff;
  text-align: center;
  color: ${Colors.darkBrown};
  font-size: 15px;
  line-height: 17px;
  font-weight: 700;

  &:first-child {
    border-left: 0px;
  }
`;

const MainControls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Table = styled.div`
  width: 100%;
  margin-left: 320px;
  div:first-child {
  }
  tbody {
    background-color: ${(props) => props.statusColor};
    tr:first-child {
      td:first-child {
        border-top-left-radius: 5px !important;
      }
      td:last-child {
        border-top-right-radius: 5px !important;
      }
    }
    tr:last-child {
      td:first-child {
        border-bottom-left-radius: 5px !important;
      }
      td:last-child {
        border-bottom-right-radius: 5px !important;
      }
    }
  }
`;

const StyledTh = styled.th`
  display: flex
  flex-direction: column;
  color: ${Colors.darkBrown};
  font-size: 15px;
  line-height: 17px;
`;

const StyledTd = styled.td`
  border: 1px solid #eeeeee;
`;

const QuantityWrapper = styled.div``;

const ActionWrapper = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  height: 100%;
  align-items: stretch;
`;

const Span = styled.span`
  font-size: 16px;
  height: 60px;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0;

  button {
    margin-left: 5px;
  }
  span {
    font-size: 18px;
  }
`;

const CellButton = styled.div`
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0;
  border-left: 1px solid #eeeeee;

  button {
    width: 100px;
  }

  .btn-link {
    color: red;
  }
`;

const ProductContent = styled.div`
  margin-bottom: 15px;
`;

const Head = styled.thead`
  th {
    text-align: center;
    width: 110px;
  }
  th:first-child {
    width: 200px !important;
    span {
      height: 20px;
    }
  }
  th:nth-child(6) {
    width: 200px !important;
  }
  th:last-child {
    width: 400px !important;
  }
`;

const Body = styled.tbody`
  td {
    text-align: center;
    width: 110px;
  }
`;

const TRow = styled.tr`
  td:first-child {
    width: 200px !important;
    // span {
    //   height: 40px;
    // }
  }
  td:last-child {
    width: 200px !important;
    opacity: 1;
  }
  &:hover td:last-child {
    opacity: 1;
  }
`;

const Quantities = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;

  p {
    padding: 5px 0;
    margin: 0;
  }
`;

const CellSmall = styled.td`
  padding: 5px 10px;
  border: 1px solid #fff;
  text-align: center;
  height: 38px;
  &:first-child {
    border-left: 0px;
    text-align: left;
    color: ${Colors.darkBrown};
    font-size: 14px;
    font-weight: 700;
  }
  &:last-child {
    border-right: 0px;
  }
`;
